import { Close, Content, Overlay, Portal, Root, Trigger } from '@radix-ui/react-dialog';
import { useLenis } from '@studio-freight/react-lenis';
import { useEffect, useRef, useState } from 'react';
import Button from '@/components/elements/Button';
import { LocaleSelectorModal } from '@/components/global/Locale/LocaleSelectorModal';
import { LocaleSelectorWidget } from '@/components/global/Locale/LocaleSelectorWidget';
import { LocaleSwitcher } from '@/components/global/Locale/LocaleSwitcher';
import { INavigationItem } from '@/graphql/__generated__/global/types';
import { SOCIAL_MEDIA } from '@/helpers/socialMedia';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useStarRvDeals } from '@/hooks/useStarRvDeals';
import classNames from '@/lib/classNames';
import { useObeUrl } from '@/lib/useObeUrl';
import { m as motion, useScroll } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { Sheet, SheetContent, SheetTrigger } from '../elements/Sheet';
import BookingSearchBar from '../shared/BookingSearchBar/BookingSearchBar';
import Container from './Container';
import { DealsBanner } from './DealsBanner';
import { Icon, IconId } from './Icons';

type NavigationItem = Pick<INavigationItem, 'title' | 'path' | 'menuAttached'>;

const variants = {
    unpinned: {
        y: '-100%',
        boxShadow: 'none',
    },
    pinned: {
        y: 0,
    },
};

export const Header = ({ navigation, showRegionPopup }: { navigation: Array<NavigationItem>; showRegionPopup?: boolean }) => {
    const [open, setOpen] = useState(false);
    const lenis = useLenis();
    const [variant, setVariant] = useState('pinned');
    const { scrollY } = useScroll();
    const isDesktop = useMediaQuery('lg');
    const ref = useRef(null);
    const bookNowUrl = useObeUrl();
    const { data: deals = [] } = useStarRvDeals();
    const bannerDeals = deals.filter((deal) => deal.showInBanner);

    useEffect(() => {
        if (open) {
            lenis?.stop();
        } else {
            lenis?.start();
        }
    }, [lenis, open]);

    useEffect(() => {
        scrollY.onChange((latest) => {
            const previous = scrollY.getPrevious();

            if (isDesktop) {
                if (latest > 40 && latest > previous) {
                    // Scrolling down
                    setVariant('unpinned');
                    document.body.classList.remove('header-pinned');
                    document.body.classList.add('header-unpinned');
                } else {
                    // Scrolling up
                    setVariant('pinned');
                    document.body.classList.remove('header-unpinned');
                    document.body.classList.add('header-pinned');
                }
            } else {
                if (ref.current && latest > 40) {
                    (ref.current as HTMLElement).classList?.add('pinned');
                } else {
                    (ref.current as unknown as HTMLElement).classList?.remove('pinned');
                }
            }
        });
    }, [scrollY, isDesktop]);

    return (
        <>
            <motion.div className="pointer-events-none fixed z-50 left-0 right-0 top-0" initial={'pinned'} variants={variants} animate={variant} transition={{ duration: 0.2 }}>
                {bannerDeals.length > 0 ? <DealsBanner deals={bannerDeals} /> : null}
                <header
                    aria-label="Site Header"
                    className={classNames(
                        'pointer-events-auto bg-primary px-4 lg:px-0 mx-4 lg:mx-0 lg:mt-0 rounded-full lg:rounded-none shadow-2xl',
                        bannerDeals.length > 0 ? 'mt-2' : 'mt-8'
                    )}
                    ref={ref}
                >
                    <Container className="md:px-4 lg:px-20 max-w-full ">
                        <div className="flex items-center justify-between h-20">
                            <Sheet open={open} onOpenChange={(value) => setOpen(value)}>
                                <SheetTrigger asChild>
                                    <button className="flex items-center lg:flex-1 group" aria-label="Open navigation menu">
                                        <div className="space-y-2">
                                            <div className="w-9 h-0.5 bg-white transition duration-500 group-hover:-translate-y-0.5"></div>
                                            <div className="w-9 h-0.5 bg-white transition duration-500 group-hover:translate-y-0.5"></div>
                                        </div>
                                        <div className="hidden lg:block ml-2 text-white">MENU</div>
                                    </button>
                                </SheetTrigger>
                                <SheetContent data-lenis-prevent>
                                    <ul className="inline-flex flex-col mt-10 lg:mt-20">
                                        {navigation.map(
                                            (item, index) =>
                                                item?.menuAttached && (
                                                    <li key={index} className="inline-flex">
                                                        <Link
                                                            href={item.path as string}
                                                            className="h2 pb-2 border-b-2 border-b-transparent hover:border-b-white transition opacity-80 hover:opacity-100"
                                                            onClick={() => setOpen(false)}
                                                            prefetch={false}
                                                        >
                                                            {item.title}
                                                        </Link>
                                                    </li>
                                                )
                                        )}
                                    </ul>
                                    <div>
                                        <div className="flex items-center gap-4 mt-16">
                                            {SOCIAL_MEDIA.map((item, itemIndex) => (
                                                <div key={itemIndex}>
                                                    <Link href={item.url} target="_blank" passHref title={`Follow on ${item.name}`} prefetch={false}>
                                                        <Icon id={item.name.toLowerCase() as IconId} className="w-8 h-8 fill-white" />
                                                    </Link>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="my-10">
                                        <LocaleSwitcher />
                                    </div>
                                    <div className="flex gap-4">
                                        <Button asChild variant="outline" colour="white">
                                            <Link target="_blank" href={process.env.NEXT_PUBLIC_CHECKIN_URL || 'https://portal.starrv.com'} prefetch={false}>
                                                My Booking
                                            </Link>
                                        </Button>
                                        <Button asChild variant="fill" colour="white">
                                            <Link target="_blank" href={bookNowUrl} prefetch={false}>
                                                Book now
                                            </Link>
                                        </Button>
                                    </div>
                                </SheetContent>
                            </Sheet>

                            <div className={classNames('flex lg:flex-1 lg:justify-center items-center -lg:mobile-header-logo')}>
                                <Link href="/" passHref title="Go to homepage" className={'transition duration-500 ease-out'} prefetch={false}>
                                    <Image priority src="/logo.svg" alt="Star RV Logo" width={130} height={100} />
                                </Link>
                                <div className="block lg:hidden">
                                    <Root>
                                        <Trigger asChild>
                                            <Button asChild variant="fill" colour="white" className={classNames('mobile-nav-button')}>
                                                <a>Book now</a>
                                            </Button>
                                        </Trigger>
                                        <Portal>
                                            <Overlay className="bg-black/70 data-[state=open]:animate-overlayShow fixed inset-0 z-50" />
                                            <Content className="flex flex-col gap-6 data-[state=open]:animate-contentShow fixed z-50 top-[50%] left-[50%] w-full max-w-[calc(100vw_-_2rem)] translate-x-[-50%] translate-y-[-50%] bg-white px-4 rounded-3xl py-10 shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none">
                                                <BookingSearchBar />
                                                <Close asChild>
                                                    <Button variant="ghost" className="text-primary bg-shell-25" aria-label="Close" size="xl">
                                                        Close X
                                                    </Button>
                                                </Close>
                                            </Content>
                                        </Portal>
                                    </Root>
                                </div>
                            </div>
                            <div className="hidden lg:flex flex-row lg:flex-1 items-center justify-end space-x-6">
                                {isDesktop ? <LocaleSelectorWidget showRegionPopup={showRegionPopup} /> : null}
                                <Button asChild variant="outline" colour="white">
                                    <Link target="_blank" href={process.env.NEXT_PUBLIC_CHECKIN_URL || 'https://portal.starrv.com'} prefetch={false}>
                                        My Booking
                                    </Link>
                                </Button>
                                <Button asChild variant="fill" colour="white">
                                    <Link target="_blank" href={bookNowUrl} prefetch={false}>
                                        Book now
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </Container>
                </header>
            </motion.div>
            {!isDesktop && showRegionPopup ? <LocaleSelectorModal /> : null}
        </>
    );
};
